<script setup lang="ts">
    import { modalKey } from './context';

    const props = withDefaults(
        defineProps<{
            /** Controlled state, for `v-model:open`. Leave unset to let the component keep its own state */
            open?: boolean;
            /** Initial state when `open` is unset */
            defaultOpen?: boolean;
        }>(),
        // Without this default Vue casts a missing `open` to `false`, and the component could never be uncontrolled
        { open: undefined },
    );

    const emit = defineEmits<{
        'update:open': [open: boolean];
    }>();

    // Used only while `open` is unset; a set `open` always wins
    const localOpen = ref(props.defaultOpen);
    const isOpen = computed(() => props.open ?? localOpen.value);

    const triggerId = useId();
    const contentId = useId();
    const titleId = useId();
    const descriptionId = useId();
    const hasTitle = ref(false);
    const hasDescription = ref(false);

    /* A modal dialog gives focus back to where it was when it opened, however it closes. */
    let returnTo: HTMLElement | null = null;
    watch(
        isOpen,
        (open) => {
            if (open) {
                returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                return;
            }
            const target = returnTo?.isConnected ? returnTo : document.getElementById(triggerId);
            returnTo = null;
            target?.focus({ preventScroll: true });
        },
        { immediate: true, flush: 'sync' },
    );

    function setOpen(next: boolean) {
        if (next === isOpen.value) return;
        localOpen.value = next;
        emit('update:open', next);
    }

    const toggle = () => setOpen(!isOpen.value);
    const close = () => setOpen(false);

    provide(modalKey, { open: isOpen, triggerId, contentId, titleId, descriptionId, hasTitle, hasDescription, setOpen, toggle, close });

    /** For a trigger that must be another component (a Button): bind these onto it */
    const triggerAttrs = computed(() => ({
        id: triggerId,
        'aria-haspopup': 'dialog' as const,
        'aria-expanded': isOpen.value,
        'aria-controls': contentId,
        'data-state': isOpen.value ? 'open' : 'closed',
        onClick: toggle,
    }));
</script>

<template>
    <!-- Renders no element of its own, like a fragment: the trigger and the content lay themselves out -->
    <slot
        :open="isOpen"
        :close="close"
        :trigger-attrs="triggerAttrs"
    />
</template>
